import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import config from '../config.js';

/** How often a pending digest is read again, in ms. */
export const DIGEST_POLL_MS = 10000;

/** The missed-opportunities report's type inside a show_report insight (ENG-202). */
export const MISSED_OPPORTUNITIES_REPORT = 'missed_opportunities';

/**
 * Where a 404 from `GET /shows/{id}/digest` leaves the page. The backend
 * answers three plain-text 404s: "Show not found", "show has not ended" and
 * "digest not found" (the show ended before the pipeline existed, which a
 * regenerate can fix).
 */
const phaseOf404 = (message = '') => {
  const text = String(message).toLowerCase();
  if (text.includes('not ended')) return 'live';
  if (text.includes('show not found')) return 'notFound';
  return 'missing';
};

const phaseOfError = (err) => {
  if (err?.status === 404) return phaseOf404(err.message);
  if (err?.status === 403) return 'forbidden';
  return 'error';
};

/**
 * The sections the digest page shows, from a complete digest's insights
 * (ENG-446): talking points, memory events (memory alerts: birthdays,
 * anniversaries, milestones, returning regulars) and the missed-opportunities
 * report, which the pipeline stores as a show_report insight (ENG-202).
 */
export const digestSections = (digest) => {
  const insights = Array.isArray(digest?.insights) ? digest.insights : [];
  const reportInsight = [...insights].reverse().find((insight) => insight.kind === 'show_report'
    && insight.data?.reportType === MISSED_OPPORTUNITIES_REPORT);
  const report = reportInsight?.data ?? null;
  return {
    talkingPoints: insights.filter((insight) => insight.kind === 'talking_point'),
    memoryEvents: insights.filter((insight) => insight.kind === 'memory_alert'),
    missedOpportunities: Array.isArray(report?.items) ? report.items : [],
    missedTruncated: Boolean(report?.truncated),
    insightsTruncated: Boolean(digest?.insightsTruncated),
  };
};

/**
 * A show's post-show digest (`GET /shows/{showID}/digest`, ENG-193), for the
 * musician's digest page (ENG-446).
 *
 * `phase` is one of:
 *   - 'loading'   the first read is out;
 *   - 'pending'   the pipeline is still assembling it; read again every
 *                 DIGEST_POLL_MS until it settles;
 *   - 'ready'     complete, with `digest`;
 *   - 'failed'    the last run failed (`errorMessage` says why, when the
 *                 backend gave a reason); a regenerate may fix it;
 *   - 'missing'   no digest exists for the show (it ended before the
 *                 pipeline did); a regenerate builds one;
 *   - 'live'      the show has not ended, so there is no digest yet;
 *   - 'notFound'  no such show;
 *   - 'forbidden' the viewer is not a member of the show's band;
 *   - 'error'     the read failed some other way; `reload()` tries again.
 *
 * `regenerate()` asks for a new run (`POST /shows/{showID}/digest/regenerate`)
 * and goes to 'pending'. A run already in progress (409) is pending too.
 * `regenerateError` is set when the request itself failed.
 */
export const useShowDigest = (showId) => {
  const fetchWrapperRef = useRef(useFetchWrapper());
  const [state, setState] = useState({ phase: 'loading', digest: null, errorMessage: '' });
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState(false);
  const seqRef = useRef(0);

  const read = useCallback(() => {
    if (!showId) return;
    const seq = ++seqRef.current;
    Promise.resolve()
      .then(() => fetchWrapperRef.current.get(`${config.apiBaseUrl}/shows/${showId}/digest`))
      .then((answer) => {
        if (seq !== seqRef.current) return;
        const status = answer?.status;
        if (status === 'complete' && answer.digest) {
          setState({ phase: 'ready', digest: answer.digest, errorMessage: '' });
        } else if (status === 'failed') {
          setState({ phase: 'failed', digest: null, errorMessage: answer.errorMessage || '' });
        } else {
          setState({ phase: 'pending', digest: null, errorMessage: '' });
        }
      })
      .catch((err) => {
        if (seq !== seqRef.current) return;
        setState({ phase: phaseOfError(err), digest: null, errorMessage: '' });
      });
  }, [showId]);

  useEffect(() => {
    setState({ phase: 'loading', digest: null, errorMessage: '' });
    read();
    return () => {
      seqRef.current += 1;
    };
  }, [read]);

  // A pending digest is read again until it is complete or failed.
  useEffect(() => {
    if (state.phase !== 'pending') return undefined;
    const timer = setTimeout(read, DIGEST_POLL_MS);
    return () => clearTimeout(timer);
  }, [state, read]);

  const regenerate = useCallback(async () => {
    if (regenerating) return;
    setRegenerating(true);
    setRegenerateError(false);
    try {
      await fetchWrapperRef.current.post(`${config.apiBaseUrl}/shows/${showId}/digest/regenerate`);
      seqRef.current += 1;
      setState({ phase: 'pending', digest: null, errorMessage: '' });
    } catch (err) {
      const text = String(err?.message ?? '').toLowerCase();
      if (err?.status === 409 && text.includes('in progress')) {
        seqRef.current += 1;
        setState({ phase: 'pending', digest: null, errorMessage: '' });
      } else if (err?.status === 409 && text.includes('not ended')) {
        setState({ phase: 'live', digest: null, errorMessage: '' });
      } else {
        setRegenerateError(true);
      }
    } finally {
      setRegenerating(false);
    }
  }, [showId, regenerating]);

  return { ...state, reload: read, regenerate, regenerating, regenerateError };
};
